import React, { FC, useContext, useRef, useEffect } from 'react';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import LoaderOverlay from '@/presentation/loader/LoaderOverlay';
import { ThemeContext } from 'styled-components';
import useKeyPressHandler from '@/hooks/useKeyPressHandler';
import { SModal } from './Modal.styled';

export { ModalResetPasswordSubmitContainer, ModalActionBar } from './Modal.styled';

interface IModalProps {
  id?: string;
  show: boolean;
  setShow: ((show: boolean) => void) | undefined;
  children: React.ReactNode;
  actionButtons?: React.ReactNode;
  buttonsLayout?: 'center' | 'end' | 'start' | 'stretched';
  isWaitingResponse?: boolean;
  notHasCloseIcon?: boolean;
  isError?: boolean;
  extraCloseStep?: () => void;
  autoFocus?: boolean;
}

const Modal: FC<IModalProps> = ({
  id = '',
  show,
  setShow,
  children,
  actionButtons = null,
  buttonsLayout = 'end',
  isWaitingResponse = false,
  notHasCloseIcon = false,
  isError = false,
  extraCloseStep,
  autoFocus = true,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const closeModal = () => {
    if (setShow) {
      if (extraCloseStep) extraCloseStep();
      setShow(false);
    }
  };
  const { onKeyPressHandler } = useKeyPressHandler(closeModal);
  const { colors } = useContext(ThemeContext);

  useEffect(() => {
    if (show && modalRef.current) {
      modalRef.current.focus();
    }
  });

  return show ? (
    <SModal onClose={closeModal} autoFocus={autoFocus}>
      <div
        id={id}
        ref={modalRef}
        onKeyDown={(e) => {
          if ((e.key === 'Escape' || e.code === 'Escape') && extraCloseStep) extraCloseStep();
        }}
      >
        {!notHasCloseIcon && (
          <FontAwesomeIcon
            className="close-button"
            onClick={closeModal}
            icon={faXmark}
            tabIndex={0}
            onKeyUp={onKeyPressHandler}
          />
        )}
        {isError && <FontAwesomeIcon icon={faTriangleExclamation} size="2x" color={colors.error} />}
        {children}
        {actionButtons && (
          <DialogActionsBar layout={buttonsLayout}>
            {isWaitingResponse && <LoaderOverlay />}
            {actionButtons}
          </DialogActionsBar>
        )}
      </div>
    </SModal>
  ) : null;
};

export default Modal;
